import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Send } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const allServices = [
  'Private Limited Company Registration',
  'LLP Registration',
  'OPC Registration',
  'Partnership Firm Registration',
  'Proprietorship Registration',
  'NGO / Trust / Section 8 Company',
  'Startup India (DPIIT) Certificate',
  'Startup Tax Exemption (80-IAC)',
  'Udyam (MSME) Registration',
  'ZED Certification',
  'Make in India Certificate',
  'GST Registration',
  'GST Returns & Annual Filing',
  'Income Tax Return (ITR) Filing',
  'TDS & TCS Compliance',
  'ROC & MCA Compliances',
  'ISO Certification (9001, 14001, 22000, 27001)',
  'BIS / ISI Registration',
  'CDSCO Drug & Medical Device License',
  'AYUSH Manufacturing & Wholesale License',
  'FSSAI License',
  'Trade License & Shop Act',
  'Trademark Registration',
  'Trademark Objection & Reply',
  'Trademark Renewal',
  'Copyright Registration',
  'Patent Registration',
  'Unsecured Business Loan',
  'CC / OD Limit from Banks & NBFCs',
  'MSME & Mudra Loans',
  'CGTMSE Loan Support',
  'Project Report (DPR) Preparation',
  'IEC Code Registration',
  'APEDA / Spice Board Registration',
  'SAFTA / COO Certificates',
  'EPR Authorization',
  'NSIC Registration',
];

export const QuickEnquiryForm = ({ isOpen, onClose, preSelectedService = '' }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    company_name: '',
    selected_services: preSelectedService ? [preSelectedService] : [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleServiceToggle = (service) => {
    setFormData(prev => ({
      ...prev,
      selected_services: prev.selected_services.includes(service)
        ? prev.selected_services.filter(s => s !== service)
        : [...prev.selected_services, service]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.selected_services.length === 0) {
      toast.error('Please select at least one service');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/quick-enquiry`, {
        ...formData,
        services: formData.selected_services.join(', ')
      });

      if (response.data.success) {
        toast.success('Enquiry submitted successfully! We will contact you soon.');
        setFormData({
          name: '',
          phone: '',
          email: '',
          company_name: '',
          selected_services: [],
        });
        onClose();
      }
    } catch (error) {
      toast.error('Failed to submit enquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl text-[#0A2463]">Quick Enquiry</DialogTitle>
          <p className="text-sm text-gray-600">Fill in your details and we'll get back to you soon</p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name" className="text-[#0A2463] font-semibold">
                Full Name *
              </Label>
              <Input
                id="name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter your name"
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="phone" className="text-[#0A2463] font-semibold">
                Phone Number *
              </Label>
              <Input
                id="phone"
                required
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+91 XXXXX XXXXX"
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-[#0A2463] font-semibold">
                Email Address *
              </Label>
              <Input
                id="email"
                required
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="your@email.com"
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="company" className="text-[#0A2463] font-semibold">
                Company Name (Optional)
              </Label>
              <Input
                id="company"
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                placeholder="Your company name"
                className="mt-2"
              />
            </div>
          </div>

          <div>
            <Label className="text-[#0A2463] font-semibold mb-3 block">
              Select Services * (Multiple Selection Allowed)
            </Label>
            <div className="border rounded-lg p-4 max-h-64 overflow-y-auto bg-gray-50">
              <div className="grid grid-cols-1 gap-2">
                {allServices.map((service) => (
                  <label
                    key={service}
                    className="flex items-center gap-3 p-2 hover:bg-white rounded cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={formData.selected_services.includes(service)}
                      onChange={() => handleServiceToggle(service)}
                      className="w-4 h-4 text-[#0A2463] focus:ring-[#247BA0] rounded"
                    />
                    <span className="text-sm text-gray-700">{service}</span>
                  </label>
                ))}
              </div>
            </div>
            {formData.selected_services.length > 0 && (
              <p className="text-xs text-[#247BA0] mt-2">
                {formData.selected_services.length} service(s) selected
              </p>
            )}
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-[#0A2463] to-[#247BA0] hover:from-[#247BA0] hover:to-[#0A2463] text-white py-6 text-lg shadow-lg"
          >
            {isSubmitting ? (
              'Submitting...'
            ) : (
              <>
                <Send className="mr-2" size={20} />
                Submit Enquiry
              </>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
